/**
 * Buddy Progression Panel
 * Shows buddy level, XP, and stat allocation
 */

import { useState } from 'react';
import { useGameStore } from '@/state';
import { allocateStatPoint, EntityStats, FishingBuddy, HaulerBuddy } from '@/types';

interface BuddyProgressionPanelProps {
  buddy: FishingBuddy | HaulerBuddy;
}

export function BuddyProgressionPanel({ buddy }: BuddyProgressionPanelProps) {
  const [showStats, setShowStats] = useState(false);
  const { stats, progress } = buddy.progression;

  const xpPercentage = (progress.currentXP / progress.xpToNextLevel) * 100;
  const hasUnspentPoints = progress.unspentStatPoints > 0;
  const buddyName = buddy.name || `Buddy ${buddy.slotNumber}`;

  // Check if this is a fishing buddy (has lastCatchAt property)
  const isFishingBuddy = 'lastCatchAt' in buddy;

  const handleAllocateStat = (stat: keyof EntityStats) => {
    useGameStore.setState((draft) => {
      const buddyList = isFishingBuddy
        ? draft.playerState.fishingBuddies
        : draft.playerState.haulerBuddies;
      const targetBuddy = buddyList.find(
        (b) => b.slotNumber === buddy.slotNumber
      );
      if (targetBuddy) {
        const newProgression = allocateStatPoint(targetBuddy.progression, stat);
        if (newProgression) {
          targetBuddy.progression = newProgression;
          console.log(`✨ ${buddyName} - Allocated stat point to ${stat}`);
        }
      }
    });
  };

  return (
    <div className="bg-gray-800/50 rounded-lg p-3 border border-blue-500/30">
      {/* Compact header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">⭐</span>
          <div>
            <h4 className="font-bold text-sm text-blue-300">Level {progress.level}</h4>
            <p className="text-xs text-gray-400">
              {progress.currentXP} / {progress.xpToNextLevel} XP
            </p>
          </div>
        </div>

        {hasUnspentPoints && (
          <div className="bg-yellow-600 text-white px-2 py-0.5 rounded-full text-xs font-bold animate-pulse">
            {progress.unspentStatPoints}
          </div>
        )}

        <button
          onClick={() => setShowStats(!showStats)}
          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs transition-colors"
        >
          {showStats ? '▲' : '▼'}
        </button>
      </div>

      {/* XP Progress bar */}
      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden mb-2">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300"
          style={{ width: `${xpPercentage}%` }}
        />
      </div>

      {/* Stats view */}
      {showStats && (
        <div className="border-t border-gray-700 pt-2 space-y-1.5">
          <h5 className="font-bold text-xs text-gray-300 mb-1">Stats:</h5>

          <div className="space-y-1">
            <StatRow
              name="Eyesight"
              icon="👁️"
              value={stats.eyesight}
              onAllocate={() => handleAllocateStat('eyesight')}
              canAllocate={hasUnspentPoints}
            />
            <StatRow
              name="Reflexes"
              icon="⚡"
              value={stats.reflexes}
              onAllocate={() => handleAllocateStat('reflexes')}
              canAllocate={hasUnspentPoints}
            />
            <StatRow
              name="Finesse"
              icon="✨"
              value={stats.finesse}
              onAllocate={() => handleAllocateStat('finesse')}
              canAllocate={hasUnspentPoints}
            />
            <StatRow
              name="Physique"
              icon="💪"
              value={stats.physique}
              onAllocate={() => handleAllocateStat('physique')}
              canAllocate={hasUnspentPoints}
            />
            <StatRow
              name="Luck"
              icon="🍀"
              value={stats.luck}
              onAllocate={() => handleAllocateStat('luck')}
              canAllocate={hasUnspentPoints}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface StatRowProps {
  name: string;
  icon: string;
  value: number;
  onAllocate: () => void;
  canAllocate: boolean;
}

function StatRow({ name, icon, value, onAllocate, canAllocate }: StatRowProps) {
  return (
    <div className="flex items-center justify-between bg-gray-700/30 px-2 py-1 rounded">
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-medium">{name}</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-blue-400 font-bold text-xs">{value}</span>
        {canAllocate && (
          <button
            onClick={onAllocate}
            className="px-2 py-0.5 bg-green-600 hover:bg-green-700 rounded text-xs font-bold transition-colors"
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}
